"use client";

import { useLanguage } from "@/components/LanguageProvider";
import { SectionRenderer } from "@/components/sections/SectionRenderer";
import { HeroThemed } from "@/components/hero/HeroThemed";
import { HeroSection } from "@/components/sections/HeroSection";
import { CTASection } from "@/components/sections/CTASection";
import type { PageSection } from "@/lib/api";
import type { SitePayload } from "@/lib/types";

type Props = {
  sections: PageSection[];
  site: SitePayload;
};

function PartnersMarquee({
  partners,
  label,
}: {
  partners: string[];
  label: string;
}) {
  return (
    <section
      className="relative overflow-hidden border-y border-slate-200/70 bg-white py-14 md:py-16"
      aria-label={label}
    >
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="flex items-center gap-4 mb-8">
          <span className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-200 to-transparent" aria-hidden />
          <p className="text-center text-[0.7rem] font-bold uppercase tracking-[0.25em] text-slate-500 md:text-xs">
            {label}
          </p>
          <span className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-200 to-transparent" aria-hidden />
        </div>
        <div className="relative overflow-hidden [mask-image:linear-gradient(90deg,transparent,black_10%,black_90%,transparent)]">
          <div className="marquee-track gap-14 md:gap-24">
            {[...partners, ...partners].map((p, i) => (
              <span
                key={`${p}-${i}`}
                className="shrink-0 whitespace-nowrap text-lg font-semibold tracking-tight text-slate-500/70 transition-colors hover:text-[var(--primary)] md:text-xl"
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function HomePageContent({ sections, site }: Props) {
  const { t, dir } = useLanguage();

  const homeSections = sections.filter((s) => s.kind !== "faq_block");

  const heroSection = homeSections.find((s) => s.kind === "hero");
  const heroPartnersRaw =
    typeof heroSection?.data?.partners === "string"
      ? heroSection.data.partners.trim()
      : "";
  const partnersRaw = heroPartnersRaw || site.homePartners?.trim() || "";
  const partners =
    partnersRaw.length > 0
      ? partnersRaw.split(",").map((p) => p.trim()).filter(Boolean)
      : [];

  if (homeSections.length > 0) {
    return (
      <div dir={dir} className="flex min-w-0 flex-col overflow-x-clip">
        {homeSections.map((section) => (
          <div key={section.id}>
            <SectionRenderer section={section} />
            {section.kind === "hero" &&
              section.id === heroSection?.id &&
              partners.length > 0 && (
                <PartnersMarquee
                  partners={partners}
                  label={t("home.partners.label")}
                />
              )}
          </div>
        ))}
      </div>
    );
  }

  const heroBadge = site.homeHeroBadge ?? t("home.hero.badge");
  const heroTitle = site.homeHeroTitle ?? t("home.hero.title");
  const heroSubtitle = site.homeHeroSubtitle ?? t("home.hero.subtitle");
  const heroCta1Label = site.homeHeroCta1Label ?? t("home.hero.cta1");
  const heroCta1Url = site.homeHeroCta1Url ?? "/services";
  const heroCta2Label = site.homeHeroCta2Label ?? t("home.hero.cta2");
  const heroCta2Url = site.homeHeroCta2Url ?? "/contact";
  const heroTheme = site.homeHeroTheme || "aurora";
  const heroBgType = site.homeHeroBackgroundType || "image";
  const heroVideoUrl = site.homeHeroVideoUrl?.trim() || "";
  const heroPosterUrl = site.homeHeroPosterUrl?.trim() || "";

  const ctaHeading = site.homeCtaHeading ?? t("home.cta.heading");
  const ctaSubtitle = site.homeCtaSubtitle ?? t("home.cta.subtitle");
  const ctaButtonLabel = site.homeCtaButtonLabel ?? t("home.cta.button");
  const ctaButtonUrl = site.homeCtaButtonUrl ?? "/contact";

  const heroContent = {
    badge: heroBadge,
    title: heroTitle,
    subtitle: heroSubtitle,
    primaryCta: heroCta1Label
      ? { label: heroCta1Label, href: heroCta1Url }
      : undefined,
    secondaryCta: heroCta2Label
      ? { label: heroCta2Label, href: heroCta2Url }
      : undefined,
  };

  return (
    <div dir={dir} className="flex flex-col">
      {heroBgType === "video" && heroVideoUrl ? (
        <HeroSection
          data={{
            background_type: "video",
            video_url: heroVideoUrl,
            poster_url: heroPosterUrl || undefined,
            badge_text: heroBadge,
            title: heroTitle,
            subtitle: heroSubtitle,
            cta_primary_label: heroCta1Label,
            cta_primary_url: heroCta1Url,
            cta_secondary_label: heroCta2Label,
            cta_secondary_url: heroCta2Url,
          }}
        />
      ) : (
        <HeroThemed theme={heroTheme} content={heroContent} />
      )}

      {partners.length > 0 ? (
        <PartnersMarquee
          partners={partners}
          label={t("home.partners.label")}
        />
      ) : null}

      <CTASection
        data={{
          heading: ctaHeading,
          subheading: ctaSubtitle,
          button_label: ctaButtonLabel,
          button_url: ctaButtonUrl,
          style: "dark",
        }}
      />
    </div>
  );
}
